import {
  Button,
  Checkbox,
  HeartHandshakeIcon,
  PencilIcon,
  Skeleton,
  TextInput,
  Typography,
} from "@/components/atoms";
import { ProgressHeader } from "@/components/molecules";
import { CountryCodeDropdown } from "@/components/molecules/CountryCodeDropdown";
import { ConfirmationDialog } from "@/components/molecules/dialogs/ConfirmationDialog";
import { OkDialog } from "@/components/molecules/dialogs/OkDialog";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useTranslation, useUIColors } from "@/core/hooks";
import { logger } from "@/core/logger";
import { useAppStore } from "@/core/stores/appStore";
import { COUNTRIES, type Country } from "@/core/utils/countryCodes";
import {
  PHONE_NUMBER_MAX_LENGTH,
  formatPhoneNumberInput,
  isValidEmail,
  validatePhoneNumber,
} from "@/core/utils/validation";
import { useRegistrationProgress } from "@/modules/onboarding/registration/utils/registrationProgress";
import { router } from "expo-router";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Dimensions,
  Keyboard,
  KeyboardAvoidingView,
  Linking,
  Platform,
  Pressable,
  Text as RNText,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useUaePassDetails } from "../hooks/useUaePassDetails";

export function UaePassDetailsScreen() {
  useCatalogDynatraceView(DT_SCREEN.UAE_PASS_DETAILS, 'uae_pass_flow_viewed', true);
  const colors = useUIColors();
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const progress = useRegistrationProgress();
  const insets = useSafeAreaInsets();
  const scrollViewRef = useRef<ScrollView>(null);
  const fieldPositions = useRef<Record<string, number>>({});
  const phoneInputRef = useRef<any>(null);
  const emailInputRef = useRef<any>(null);
  const screenHeight = Dimensions.get("window").height;

  const {
    user,
    isLoadingDetails,
    detailsError,
    shouldSkipOTP,
    emailMatches,
    phoneMatches,
    maskedFirstName,
    maskedMiddleName,
    maskedLastName,
    maskedEmail,
    maskedPhone,
    parsedPhone,
    fetchCustomerDetails,
    setDetailsError,
    updateLocalSalesforceData,
  } = useUaePassDetails();

  useEffect(() => {
    if (!user?.idn || !user?.nationalityEN) {
      logger.debug("UaePassDetailsScreen", "Skip fetch - missing identifiers");
      return;
    }

    let isMounted = true;

    fetchCustomerDetails().catch((error) => {
      if (!isMounted) {
        return;
      }
      logger.error("UaePassDetailsScreen", "Failed to fetch customer details", {
        error,
      });
    });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.idn, user?.nationalityEN]);

  const selectedCountry = useMemo(() => {
    const country = COUNTRIES.find((c) => c.code === parsedPhone.code);
    return (country ?? COUNTRIES[0])!;
  }, [parsedPhone.code]);

  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPhoneEditable, setIsPhoneEditable] = useState(false);
  const [isEmailEditable, setIsEmailEditable] = useState(false);
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [hasEditedPhone, setHasEditedPhone] = useState(false);
  const [hasEditedEmail, setHasEditedEmail] = useState(false);

  useEffect(() => {
    const showSubscription = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow",
      (event) => setKeyboardHeight(event.endCoordinates.height),
    );
    const hideSubscription = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide",
      () => setKeyboardHeight(0),
    );

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, []);

  const handleBackPress = () => {
    router.back();
  };

  const handleFieldLayout = (field: "phone" | "email", event: any) => {
    fieldPositions.current[field] = event.nativeEvent.layout.y;
  };

  const handleFieldFocus = useCallback(
    (field: "phone" | "email") => {
      const fieldY = fieldPositions.current[field];
      if (fieldY === undefined || !scrollViewRef.current) {
        return;
      }

      setTimeout(
        () => {
          const headerHeight = 140;
          const buttonArea = Math.max(insets.bottom, 45) + 140;
          const currentKeyboardHeight = keyboardHeight || 300;
          const availableHeight =
            screenHeight - currentKeyboardHeight - buttonArea - headerHeight;
          const padding = 80;
          const target = Math.max(0, fieldY - availableHeight + padding);
          scrollViewRef.current?.scrollTo({ y: target, animated: true });
        },
        Platform.OS === "ios" ? 350 : 200,
      );
    },
    [insets.bottom, keyboardHeight, screenHeight],
  );

  const handleVerifyPress = () => {
    if (!isTermsAccepted) {
      return;
    }

    // If all fields match and nothing was edited, skip OTP verification
    if (shouldSkipOTP && !hasEditedEmail && !hasEditedPhone) {
      router.replace("/registration-success");
      return;
    }

    // Determine which OTP verification is needed based on:
    // 1. If email matches AND email hasn't been edited → email verification not required
    // 2. If phone matches AND phone hasn't been edited → phone verification not required
    // 3. Otherwise, verification is required

    const needsEmailVerification = !emailMatches || hasEditedEmail;
    const needsPhoneVerification = !phoneMatches || hasEditedPhone;

    // Priority: If email needs verification, do email first. Otherwise, do phone.
    if (needsEmailVerification) {
      router.push("/email-otp");
      return;
    }

    if (needsPhoneVerification) {
      router.push("/phone-otp");
      return;
    }

    // If neither needs verification (shouldn't happen, but fallback)
    router.replace("/registration-success");
  };

  const handleCountrySelect = useCallback((_country: Country) => {
    // Country code comes from Salesforce data. Manual change is disabled per Figma.
  }, []);

  const handlePhoneEdit = () => {
    setShowPhoneDialog(true);
  };

  const handleEmailEdit = () => {
    setShowEmailDialog(true);
  };

  const confirmPhoneEdit = () => {
    setShowPhoneDialog(false);
    setIsPhoneEditable(true);
    setPhoneNumber("");
    setTimeout(() => phoneInputRef.current?.focus(), 100);
  };

  const confirmEmailEdit = () => {
    setShowEmailDialog(false);
    setIsEmailEditable(true);
    setEmail("");
    setTimeout(() => emailInputRef.current?.focus(), 100);
  };

  const saveEditedPhone = (value: string) => {
    const digitsOnly = value.replace(/\D/g, "");
    const validationMessage = validatePhoneNumber(digitsOnly);
    if (validationMessage) {
      setPhoneError(validationMessage);
      return false;
    }

    const normalized = `+${selectedCountry.code.replace("+", "")}${digitsOnly}`;
    const currentState = useAppStore.getState();
    const registration = currentState.uaePassRegistration;

    if (!registration?.user) {
      return true;
    }

    currentState.setUaePassRegistration({
      user: {
        ...registration.user,
        mobile: normalized,
      },
      ...(registration.registrationId && {
        registrationId: registration.registrationId,
      }),
      ...(registration.customerId && { customerId: registration.customerId }),
    });
    updateLocalSalesforceData({ phone: normalized });
    setHasEditedPhone(true);
    setPhoneError("");
    logger.info("UaePassDetailsScreen", "Saved edited phone to store");
    return true;
  };

  const saveEditedEmail = (value: string) => {
    if (!isValidEmail(value)) {
      setEmailError(translate("registration.enterValidEmail"));
      return false;
    }

    const currentState = useAppStore.getState();
    const registration = currentState.uaePassRegistration;

    if (!registration?.user) {
      return true;
    }

    currentState.setUaePassRegistration({
      user: {
        ...registration.user,
        email: value,
      },
      ...(registration.registrationId && {
        registrationId: registration.registrationId,
      }),
      ...(registration.customerId && { customerId: registration.customerId }),
    });
    updateLocalSalesforceData({ email: value });
    setHasEditedEmail(true);
    setEmailError("");
    logger.info("UaePassDetailsScreen", "Saved edited email to store");
    return true;
  };

  const handlePhoneBlur = () => {
    if (!isPhoneEditable) {
      return;
    }

    if (!phoneNumber.trim()) {
      setIsPhoneEditable(false);
      setPhoneError("");
      return;
    }

    const didSave = saveEditedPhone(phoneNumber.trim());
    if (didSave) {
      setIsPhoneEditable(false);
      setPhoneNumber("");
    } else {
      requestAnimationFrame(() => phoneInputRef.current?.focus());
    }
  };

  const handleEmailBlur = () => {
    if (!isEmailEditable) {
      return;
    }

    if (!email.trim()) {
      setIsEmailEditable(false);
      setEmailError("");
      return;
    }

    const didSave = saveEditedEmail(email.trim());
    if (didSave) {
      setIsEmailEditable(false);
      setEmail("");
    } else {
      requestAnimationFrame(() => emailInputRef.current?.focus());
    }
  };

  const handleTapOutside = () => {
    Keyboard.dismiss();
    handlePhoneBlur();
    handleEmailBlur();
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.backgroundLevel1,
    },
    keyboardAvoidingView: {
      flex: 1,
    },
    scrollView: {
      flex: 1,
    },
    scrollContentContainer: {
      flexGrow: 1,
    },
    contentContainer: {
      paddingHorizontal: 16,
      paddingTop: 32,
      paddingBottom: 20,
      flex: 1,
      gap: 32,
    },
    headerSection: {
      gap: 12,
    },
    iconContainer: {
      alignSelf: "flex-start",
      padding: 8,
    },
    titleContainer: {
      alignItems: "flex-start",
    },
    descriptionContainer: {
      alignItems: "flex-start",
    },
    formSection: {
      gap: 32,
    },
    nameSection: {
      gap: 16,
    },
    staticField: {
      gap: 4,
    },
    fieldLabel: {
      fontSize: 13,
      lineHeight: 16,
    },
    fieldValue: {
      fontSize: 16,
      lineHeight: 20,
    },
    phoneSection: {
      gap: 8,
    },
    phoneContainer: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
    },
    phoneInput: {
      flex: 1,
    },
    editIcon: {
      padding: 8,
    },
    errorText: {
      marginTop: 4,
    },
    emailSection: {
      gap: 8,
    },
    skeletonRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
    },
    termsSection: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 8,
      paddingHorizontal: 16,
      paddingBottom: 16,
    },
    termsText: {
      flex: 1,
    },
    termsTextContent: {
      flexDirection: "row",
      flexWrap: "wrap",
    },
    termsTextRegular: {
      fontSize: 15,
      lineHeight: 20,
      color: colors.fontSecondary,
      fontFamily: "Matter",
    },
    termsLink: {
      fontSize: 15,
      lineHeight: 20,
      color: colors.fontPrimary,
      fontFamily: "Matter-SemiBold",
      textDecorationLine: "underline",
    },
    bottomSection: {
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: colors.backgroundLevel1,
      borderTopWidth: 1,
      borderTopColor: colors.backgroundLevel2,
    },
    buttonSection: {
      paddingHorizontal: 16,
      paddingTop: 12,
      paddingBottom: 16,
    },
  });

  return (
    <TouchableWithoutFeedback onPress={handleTapOutside}>
      <View style={styles.container}>
        <ProgressHeader
          progress={progress}
          showBackButton
          onBackPress={handleBackPress}
          color={colors.fontPrimary}
          showLanguageSwitch
        />

        <KeyboardAvoidingView
          style={styles.keyboardAvoidingView}
          behavior={Platform.OS === "ios" ? "padding" : undefined}
          keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 0}
          enabled={Platform.OS === "ios"}
        >
          <ScrollView
            ref={scrollViewRef}
            style={styles.scrollView}
            contentContainerStyle={[
              styles.scrollContentContainer,
              {
                paddingBottom: keyboardHeight > 0 ? keyboardHeight + 160 : 160,
              },
            ]}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
          >
            <View style={styles.contentContainer}>
              {isLoadingDetails ? (
                <>
                  <View style={styles.headerSection}>
                    <View style={styles.iconContainer}>
                      <Skeleton width={48} height={48} variant="circular" />
                    </View>
                    <View style={styles.titleContainer}>
                      <Skeleton width={160} height={28} />
                    </View>
                    <View style={styles.descriptionContainer}>
                      <Skeleton width={260} height={20} />
                    </View>
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.nameSection}>
                      {[80, 90, 70].map((width, index) => (
                        <View key={index} style={styles.staticField}>
                          <Skeleton
                            width={width}
                            height={16}
                            style={{ marginBottom: 4 }}
                          />
                          <Skeleton width={width + 80} height={20} />
                        </View>
                      ))}
                    </View>

                    <View style={styles.phoneSection}>
                      <Skeleton width={140} height={16} />
                      <View style={styles.skeletonRow}>
                        <Skeleton width={80} height={48} borderRadius={8} />
                        <View style={{ flex: 1 }}>
                          <Skeleton width="100%" height={48} borderRadius={8} />
                        </View>
                        <Skeleton width={36} height={36} variant="circular" />
                      </View>
                    </View>

                    <View style={styles.emailSection}>
                      <Skeleton width={120} height={16} />
                      <View style={styles.skeletonRow}>
                        <View style={{ flex: 1 }}>
                          <Skeleton width="100%" height={48} borderRadius={8} />
                        </View>
                        <Skeleton width={36} height={36} variant="circular" />
                      </View>
                    </View>
                  </View>
                </>
              ) : (
                <>
                  <View style={styles.headerSection}>
                    <View style={styles.iconContainer}>
                      <HeartHandshakeIcon />
                    </View>
                    <View style={styles.titleContainer}>
                      <Typography variant="title1" color="primary">
                        {t("registration.hello")}
                      </Typography>
                    </View>
                    <View style={styles.descriptionContainer}>
                      <Typography variant="body" color="secondary">
                        {t("registration.detailsDescription")}
                      </Typography>
                    </View>
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.nameSection}>
                      <View style={styles.staticField}>
                        <Typography
                          variant="footnote"
                          color="secondary"
                          style={styles.fieldLabel}
                        >
                          {t("profile.firstName")}
                        </Typography>
                        <Typography
                          variant="body"
                          color="primary"
                          style={styles.fieldValue}
                        >
                          {maskedFirstName}
                        </Typography>
                      </View>
                      <View style={styles.staticField}>
                        <Typography
                          variant="footnote"
                          color="secondary"
                          style={styles.fieldLabel}
                        >
                          {t("profile.middleName")}
                        </Typography>
                        <Typography
                          variant="body"
                          color="primary"
                          style={styles.fieldValue}
                        >
                          {maskedMiddleName}
                        </Typography>
                      </View>
                      <View style={styles.staticField}>
                        <Typography
                          variant="footnote"
                          color="secondary"
                          style={styles.fieldLabel}
                        >
                          {t("profile.lastName")}
                        </Typography>
                        <Typography
                          variant="body"
                          color="primary"
                          style={styles.fieldValue}
                        >
                          {maskedLastName}
                        </Typography>
                      </View>
                    </View>

                    <View
                      style={styles.phoneSection}
                      onLayout={(event) => handleFieldLayout("phone", event)}
                    >
                      <Typography variant="footnote" color="secondary">
                        {t("profile.phoneNumber")}
                      </Typography>
                      <View style={styles.phoneContainer}>
                        <CountryCodeDropdown
                          selectedCountry={selectedCountry}
                          onCountrySelect={handleCountrySelect}
                          disabled={!isPhoneEditable}
                        />
                        <View style={styles.phoneInput}>
                          <TextInput
                            ref={phoneInputRef}
                            placeholder={
                              isPhoneEditable
                                ? t("profile.enterPhoneNumber")
                                : ""
                            }
                            value={isPhoneEditable ? phoneNumber : maskedPhone}
                            onChangeText={(text) => {
                              // Format input to enforce max length and remove non-digits
                              const formatted = formatPhoneNumberInput(text);
                              setPhoneNumber(formatted);
                              setPhoneError("");
                            }}
                            keyboardType="phone-pad"
                            editable={isPhoneEditable}
                            onBlur={handlePhoneBlur}
                            onFocus={() => handleFieldFocus("phone")}
                            maxLength={PHONE_NUMBER_MAX_LENGTH}
                          />
                          {!!phoneError && (
                            <Typography
                              variant="footnote"
                              color="error"
                              style={styles.errorText}
                            >
                              {phoneError}
                            </Typography>
                          )}
                        </View>
                        <TouchableOpacity
                          style={styles.editIcon}
                          onPress={handlePhoneEdit}
                        >
                          <PencilIcon size={20} color={colors.fontSecondary} />
                        </TouchableOpacity>
                      </View>
                    </View>

                    <View
                      style={styles.emailSection}
                      onLayout={(event) => handleFieldLayout("email", event)}
                    >
                      <Typography variant="footnote" color="secondary">
                        {t("registration.email")}
                      </Typography>
                      <View style={styles.phoneContainer}>
                        <View style={styles.phoneInput}>
                          <TextInput
                            ref={emailInputRef}
                            placeholder={
                              isEmailEditable
                                ? t("profile.enterEmailAddress")
                                : ""
                            }
                            value={isEmailEditable ? email : maskedEmail}
                            onChangeText={(text) => {
                              setEmail(text);
                              setEmailError("");
                            }}
                            keyboardType="email-address"
                            autoCapitalize="none"
                            editable={isEmailEditable}
                            onBlur={handleEmailBlur}
                            onFocus={() => handleFieldFocus("email")}
                          />
                          {!!emailError && (
                            <Typography
                              variant="footnote"
                              color="error"
                              style={styles.errorText}
                            >
                              {emailError}
                            </Typography>
                          )}
                        </View>
                        <TouchableOpacity
                          style={styles.editIcon}
                          onPress={handleEmailEdit}
                        >
                          <PencilIcon size={20} color={colors.fontSecondary} />
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>
                </>
              )}
            </View>
          </ScrollView>
        </KeyboardAvoidingView>

        {!isLoadingDetails && (
          <View
            style={[
              styles.bottomSection,
              { paddingBottom: Math.max(insets.bottom, 16) },
            ]}
          >
            <View style={styles.termsSection}>
              <Checkbox
                isSelected={isTermsAccepted}
                onPress={() => setIsTermsAccepted((prev) => !prev)}
                size={24}
              />
              <View style={styles.termsText}>
                <RNText style={styles.termsTextContent}>
                  <RNText style={styles.termsTextRegular}>
                    {translate("registration.termsPrefix")}{" "}
                  </RNText>
                  <Pressable
                    onPress={() =>
                      Linking.openURL("https://bloomholding.com/terms-of-use")
                    }
                  >
                    <RNText style={styles.termsLink}>
                      {translate("registration.termsLink")}
                    </RNText>
                  </Pressable>
                  <RNText
                    style={styles.termsTextRegular}
                  >{` ${translate("registration.andText")} `}</RNText>
                  <Pressable
                    onPress={() =>
                      Linking.openURL("https://bloomholding.com/privacy-policy")
                    }
                  >
                    <RNText style={styles.termsLink}>
                      {translate("registration.privacyLink")}
                    </RNText>
                  </Pressable>
                </RNText>
              </View>
            </View>
            <View style={styles.buttonSection}>
              <Button
                title={t("common.verify")}
                variant="primary"
                fullWidth
                onPress={handleVerifyPress}
                disabled={!isTermsAccepted}
              />
            </View>
          </View>
        )}

        <ConfirmationDialog
          visible={showPhoneDialog}
          type="phone"
          message={t("dialogs.changePhoneConfirmation")}
          onConfirm={confirmPhoneEdit}
          onCancel={() => setShowPhoneDialog(false)}
        />

        <ConfirmationDialog
          visible={showEmailDialog}
          type="email"
          message={t("dialogs.changeEmailConfirmation")}
          onConfirm={confirmEmailEdit}
          onCancel={() => setShowEmailDialog(false)}
        />

        <OkDialog
          visible={!!detailsError}
          message={detailsError || ""}
          onOk={() => setDetailsError(null)}
        />
      </View>
    </TouchableWithoutFeedback>
  );
}

export default UaePassDetailsScreen;
